import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import {
  STAGE_LABEL,
  STATUS_CHANGE_REASON_LABEL,
  STATUS_LABEL,
  STATUS_TONE,
  label,
} from '../lib/vocabulary.ts'
import { Button } from './Button.tsx'
import { icons } from './icons.ts'

export interface StatusStop {
  readonly status: string
  readonly at: string
  /** Where the requisition was; absent on entries from before #489. */
  readonly stage?: string | undefined
  /** Why, when the status alone does not say (a reopen). */
  readonly reason?: string | undefined
}

/**
 * The statuses, as stops on a line.
 *
 * Every status the verification has been in, oldest first, from the row's own
 * record: the name above a dot coloured as the queue colours the status, the
 * moment small beneath it. The shape is the review's progress rail, so the
 * two read as the same device. A status that came back -- reverified twice --
 * appears twice, because it happened twice.
 *
 * **Every name has its own room, and nothing wraps.** A stop is as wide as
 * its name; the strip shows as many whole stops as fit its width, measured
 * from the rendered stops rather than assumed, spreads them across the
 * strip the way the review rail does, and the arrows slide it one stop at a
 * time. It opens on the latest, which is the one somebody asking "where is
 * it now" wants first.
 *
 * **A past "Aguardando conferência" reads "Conferindo"** when what came next
 * was a conclusion: the wait was the time somebody spent conferring it, and
 * the name that was true then is not the name that is useful now. Followed
 * by a reverify, a release or a cancel, it keeps its name -- nobody conferred
 * it.
 */
export function StatusTimeline({
  statuses: recorded,
  highlighted,
}: {
  statuses: readonly StatusStop[]
  /** The stop to light up, e.g. the one a hovered decision happened in. */
  highlighted?: number | undefined
}) {
  // Memoised: the layout effect keys on it, and a fresh array every render
  // would run the effect every render.
  const statuses = useMemo(() => onRail(recorded), [recorded])
  const strip = useRef<HTMLDivElement>(null)
  const stops = useRef<Array<HTMLLIElement | null>>([])
  const dots = useRef<Array<HTMLSpanElement | null>>([])
  const [first, setFirst] = useState(0)
  const [layout, setLayout] = useState({
    start: 0,
    count: 0,
    gap: 0,
    offset: 0,
    railTop: 0,
    railLeft: 0,
    railWidth: 0,
  })
  // Open at the end, and again after a resize: the latest status is the answer.
  const [anchoredToEnd, setAnchoredToEnd] = useState(true)
  const [stripWidth, setStripWidth] = useState(0)

  useLayoutEffect(() => {
    const element = strip.current
    if (element === null) return
    const observer = new ResizeObserver(() => {
      setStripWidth(element.clientWidth)
      setAnchoredToEnd(true)
    })
    observer.observe(element)
    setStripWidth(element.clientWidth)
    return () => observer.disconnect()
  }, [])

  useLayoutEffect(() => {
    const widths = stops.current.slice(0, statuses.length).map((li) => li?.offsetWidth ?? 0)
    const room = stripWidth
    if (widths.length === 0 || room === 0) return

    const fitsFrom = (start: number) => {
      let used = 0
      let n = 0
      for (let i = start; i < widths.length; i += 1) {
        used += widths[i]!
        if (used > room && n > 0) break
        n += 1
      }
      return Math.max(1, n)
    }
    const fitsEndingAt = (end: number) => {
      let used = 0
      let start = end
      for (let i = end; i >= 0; i -= 1) {
        used += widths[i]!
        if (used > room && start < end) break
        start = i
      }
      return start
    }

    let start = first
    if (anchoredToEnd) {
      start = fitsEndingAt(widths.length - 1)
      setAnchoredToEnd(false)
      setFirst(start)
    } else if (highlighted !== undefined && highlighted < start) {
      // A lit stop off to the left or right is brought into view for as
      // long as it is lit; `first` keeps the resting position, so the rail
      // goes back where it was when the pointer leaves.
      start = highlighted
    } else if (highlighted !== undefined && highlighted >= start + fitsFrom(start)) {
      start = fitsEndingAt(highlighted)
    }

    const shown = fitsFrom(start)
    const last = start + shown - 1
    const shownWidth = widths.slice(start, last + 1).reduce((sum, w) => sum + w, 0)
    // Spread across the strip, the way the review rail does, so the last
    // stop sits at the end instead of leaving the remainder empty.
    const gap = shown > 1 ? Math.max(0, (room - shownWidth) / (shown - 1)) : 0
    const before = widths.slice(0, start).reduce((sum, w) => sum + w, 0) + start * gap
    const dot = dots.current[start]
    const railTop = dot === null || dot === undefined ? 0 : dot.offsetTop + dot.offsetHeight / 2 - 1
    const railLeft = widths[start]! / 2
    const railRight = shown > 1 ? room - widths[last]! / 2 : railLeft

    setLayout({
      start,
      count: shown,
      gap,
      offset: before,
      railTop,
      railLeft,
      railWidth: Math.max(0, railRight - railLeft),
    })
  }, [first, anchoredToEnd, stripWidth, statuses, highlighted])

  const count = layout.count === 0 ? statuses.length : layout.count
  const overflowing = count < statuses.length
  const viewFirst = layout.count === 0 ? first : layout.start
  const last = viewFirst + count

  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-tSmall text-text-weak uppercase tracking-[0.04em]">Linha do tempo</h3>
      <div className="flex items-center gap-1">
        {overflowing && (
          <Button
            size="icon"
            variant="tertiary"
            tone="neutral"
            leadingIcon={icons.back}
            aria-label="Estados anteriores"
            disabled={first === 0}
            onClick={() => setFirst((current) => Math.max(0, current - 1))}
          />
        )}
        <div ref={strip} className="relative min-w-0 flex-1 overflow-hidden">
          {/* The rail behind the dots, from the first shown centre to the last. */}
          {count > 1 && (
            <span
              aria-hidden="true"
              className="bg-stroke-weak absolute h-0.5"
              style={{ top: layout.railTop, left: layout.railLeft, width: layout.railWidth }}
            />
          )}
          <ol
            className="relative flex"
            style={{ columnGap: layout.gap, transform: `translateX(-${layout.offset}px)` }}
          >
            {statuses.map((step, index) => {
              const latest = index === statuses.length - 1
              const lit = index === highlighted
              const shown = index >= viewFirst && index < last
              const tone = STATUS_TONE[step.status]
              const color = tone?.dot ?? 'var(--color-text-weak)'
              return (
                <li
                  key={`${step.at}-${index}`}
                  ref={(element) => {
                    stops.current[index] = element
                  }}
                  className="flex shrink-0 flex-col items-center gap-1 px-2"
                  style={{ visibility: shown ? 'visible' : 'hidden' }}
                  aria-hidden={!shown}
                >
                  <span
                    className={`text-tSmall whitespace-nowrap ${lit ? 'font-semibold' : ''}`}
                    style={{ color: tone?.text ?? 'var(--color-text-weak)' }}
                  >
                    {nameOf(statuses, index)}
                  </span>
                  <span
                    ref={(element) => {
                      dots.current[index] = element
                    }}
                    aria-hidden="true"
                    className="block size-4 rounded-full border-2 transition-shadow"
                    style={{
                      borderColor: color,
                      background: lit || latest ? color : 'var(--color-fill-white)',
                      boxShadow: lit
                        ? `0 0 0 4px ${color}66`
                        : latest
                          ? '0 0 0 3px rgba(77,100,217,0.25)'
                          : 'none',
                    }}
                  />
                  <span className="text-text-weak text-[10px] leading-[14px] whitespace-nowrap tabular-nums">
                    {when(step.at)}
                  </span>
                  {step.stage !== undefined && (
                    <span className="text-text-weak text-[10px] leading-[14px] whitespace-nowrap">
                      {label(STAGE_LABEL, step.stage)}
                    </span>
                  )}
                </li>
              )
            })}
          </ol>
        </div>
        {overflowing && (
          <Button
            size="icon"
            variant="tertiary"
            tone="neutral"
            leadingIcon={icons.forward}
            aria-label="Estados seguintes"
            disabled={first + count >= statuses.length}
            onClick={() => setFirst((current) => Math.min(statuses.length - 1, current + 1))}
          />
        )}
      </div>
    </section>
  )
}

/**
 * "Concluindo…" is the second between pressing Concluir and the result being
 * written; every conclusion passes through it, so on the rail it is only a
 * step between "Conferido" and what it became. Kept in the record, not shown.
 */
const PASSING = new Set(['concluding'])

function onRail(statuses: readonly StatusStop[]): readonly StatusStop[] {
  return statuses.filter((step) => !PASSING.has(step.status))
}

/** What ended the wait: these mean somebody conferred it. */
const CONCLUDED_BY = new Set(['concluding', 'verified', 'awaiting_correction'])

function nameOf(statuses: readonly StatusStop[], index: number): string {
  const step = statuses[index]!
  // A reopen says so: "Na fila" alone reads as a new verification.
  if (step.reason !== undefined) return label(STATUS_CHANGE_REASON_LABEL, step.reason)
  const next = statuses[index + 1]
  if (step.status === 'awaiting_verifier' && next !== undefined && CONCLUDED_BY.has(next.status)) {
    return 'Conferindo'
  }
  return label(STATUS_LABEL, step.status)
}

/**
 * The stop a moment happened in: the latest status entered at or before it.
 * Undefined when the moment is unknown or precedes every status.
 */
export function stopAt(
  statuses: readonly StatusStop[],
  iso: string | undefined,
): number | undefined {
  if (iso === undefined || iso === '') return undefined
  const moment = new Date(iso).getTime()
  if (Number.isNaN(moment)) return undefined
  let found: number | undefined
  onRail(statuses).forEach((step, index) => {
    if (new Date(step.at).getTime() <= moment) found = index
  })
  return found
}

function when(iso: string): string {
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return iso
  return at.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}
